import { BreakdownPriceTier, FormattedBreakdownPriceTier } from "./common.ts";
import { PriceFinderContextValue } from "./context.tsx";

export const getUnitCount = (ctx: PriceFinderContextValue) =>
    ctx.inputValues.unitCount;
export const getBillingCycle = (ctx: PriceFinderContextValue) =>
    ctx.inputValues.billingCycle;

export const hasAnnualBilling = (ctx: PriceFinderContextValue) => {
    return ctx.config.billingCycleConfig.annual;
};
export const hasMonthlyBilling = (ctx: PriceFinderContextValue) => {
    return ctx.config.billingCycleConfig.monthly;
};

export const isUnitCountAboveMaxThreshold = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const unitCount = getUnitCount(ctx);
    const planLimitConfig = getPlanLimitConfig(ctx, offeringSku);
    return unitCount > planLimitConfig.signupLimit;
};

export const isUnitCountBelowMinThreshold = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    throw new Error("not yet implemented");
};

/**
 * @deprecated this should be replaced by isUnitCountBelowMinThreshold
 */
export const isUnitCountAboveEnterpriseMinThreshold = (
    ctx: PriceFinderContextValue
) => {
    const unitCount = getUnitCount(ctx);
    const unitMinEnterprise = ctx.config.unitMinEnterprise;
    return unitCount >= unitMinEnterprise;
};

const getPlanLimitConfigs = (ctx: PriceFinderContextValue) => {
    return ctx.config.planLimitConfigs;
};

const getPlanLimitConfig = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const planLimitConfigs = getPlanLimitConfigs(ctx);
    const planLimitConfig = planLimitConfigs.find(
        (plan) => plan.offeringSku === offeringSku
    );
    if (!planLimitConfig) {
        throw new Error("unexpected empty value");
    }
    return planLimitConfig;
};

const getPlanPriceResult = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const planPriceResult = ctx.calculationResult.find(
        (plan) => plan.offeringSku === offeringSku
    );
    if (!planPriceResult) {
        throw new Error("unexpected empty value");
    }
    return planPriceResult;
};

export const getPlanTotalPriceFormatted = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const price = getPlanPriceResult(ctx, offeringSku);
    return price.formattedTotalPrice;
};

export const getPlanAvgPerUnitPrice = (
    ctx: PriceFinderContextValue,
    offeringSku: string
): number => {
    const price = getPlanPriceResult(ctx, offeringSku);
    return price.avgPerUnitPrice;
};

export const roundPrice = (price: number): number => {
    return Math.round(price);
};

export const roundJPYPrice = (price: number): number => {
    return Math.ceil(price);
};

export const getNumberFormatLocale = (ctx: PriceFinderContextValue) => {
    const currencyISOCode = getCurrencyIsoCode(ctx);
    const isJPY = currencyISOCode === "JPY";
    return isJPY ? "ja-JP" : "en-US";
};

export const formatPrice = (
    ctx: PriceFinderContextValue,
    price: number,
    options: { minimumFractionDigits?: number } = {}
): string => {
    const numberFormatLocale = getNumberFormatLocale(ctx);
    const currencyISOCode = getCurrencyIsoCode(ctx);
    return new Intl.NumberFormat(numberFormatLocale, {
        style: "currency",
        currency: currencyISOCode,
        minimumFractionDigits: options.minimumFractionDigits ?? 0,
        maximumFractionDigits: options.minimumFractionDigits ?? 0
    }).format(price);
};

export const getPlanAvgPerUnitPriceFormatted = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const price = getPlanPriceResult(ctx, offeringSku);
    return price.formattedAvgPerUnitPrice;
};

export const isOfferingRecommended = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const recommendedOfferingSku = getRecommendedOfferingSku(ctx);
    return recommendedOfferingSku === offeringSku;
};

const getRecommendedOfferingSku = (ctx: PriceFinderContextValue) => {
    const getRecommendedPlanIdx = (
        unitCount: number,
        minUnitCounts: number[]
    ) => {
        const len = minUnitCounts.length - 1;
        return (
            len -
            minUnitCounts
                .reverse()
                .findIndex((minUnitCount) => unitCount > minUnitCount)
        );
    };

    const unitCount = getUnitCount(ctx);
    const planLimitConfigs = getPlanLimitConfigs(ctx);
    const recommendedPlanIdx = getRecommendedPlanIdx(
        unitCount,
        planLimitConfigs.map((productPlan) => productPlan.recommendationLimit)
    );
    return planLimitConfigs[recommendedPlanIdx]?.offeringSku;
};

const getUnformattedBreakdown = (
    ctx: PriceFinderContextValue,
    offeringSku: string
): BreakdownPriceTier | undefined => {
    const planPriceResult = getPlanPriceResult(ctx, offeringSku);
    return planPriceResult.breakdown.at(-1);
};
export const getUnitTierLowerBound = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const breakdown = getUnformattedBreakdown(ctx, offeringSku);
    return breakdown?.unitLimitLower;
};
export const getUnitTierUpperBound = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const breakdown = getUnformattedBreakdown(ctx, offeringSku);
    return breakdown?.unitLimitUpper;
};

/**
 * @deprecated
 */
const getFormattedBreakdown = (
    ctx: PriceFinderContextValue,
    offeringSku: string
): FormattedBreakdownPriceTier | undefined => {
    const planPriceResult = getPlanPriceResult(ctx, offeringSku);
    return planPriceResult.formattedBreakdown.at(-1);
};

/**
 * @deprecated
 */
export const getUnitTierLowerBoundFormatted = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const breakdown = getFormattedBreakdown(ctx, offeringSku);
    return breakdown?.formattedUnitLimitLower;
};

/**
 * @deprecated
 */
export const getUnitTierUpperBoundFormatted = (
    ctx: PriceFinderContextValue,
    offeringSku: string
) => {
    const breakdown = getFormattedBreakdown(ctx, offeringSku);
    return breakdown?.formattedUnitLimitUpper;
};

export const getCurrencyIsoCode = (ctx: PriceFinderContextValue) =>
    ctx.config.currencyISOCode;

export const getRoundedFormattedPlanPrice = (
    ctx: PriceFinderContextValue,
    offeringSku: string
): string => {
    const billingCycle = getBillingCycle(ctx);
    const currencyISOCode = getCurrencyIsoCode(ctx);

    if (billingCycle === "ANNUAL") {
        return getPlanTotalPriceFormatted(ctx, offeringSku);
    }

    const rawPrice = getPlanAvgPerUnitPrice(ctx, offeringSku);
    const roundedPrice =
        currencyISOCode === "JPY"
            ? roundJPYPrice(rawPrice)
            : roundPrice(rawPrice);

    // Format with no decimals for rounded prices
    return formatPrice(ctx, roundedPrice);
};
